import {
  type IListUserOption,
  type IMeshInfo,
  isMeshInfoOriginalMaterial,
  type SupplyPartDefinition,
} from "@project/emerge-types";
import { memo, Suspense, useContext, useEffect, useMemo, useRef } from "react";
import {
  BoxGeometry,
  DoubleSide,
  type Group,
  type Material,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  Object3D,
} from "three";
import { useShallow } from "zustand/react/shallow";
import { ThreeToolsInstance } from "../util/ThreeTools";
import { ManufacturerContext } from "./ManufacturerProvider";
import { useProductStoreContext } from "./ProductStoreContext";
import {
  PRODUCT_SCENE_LOADING_BOUNDS_NAME,
  trackProductSceneMeshLoad,
} from "./productViewerCameraFit";

// Type guard to check if a material has a specific property
// biome-ignore lint/suspicious/noExplicitAny: type guard
function hasProperty<T extends Material, K extends keyof any>(
  material: T,
  property: K,
  // biome-ignore lint/suspicious/noExplicitAny: type guard
): material is T & Record<K, any> {
  return property in material;
}

// Hilfsfunktion zum Umwandeln von Legacy-Materialien in moderne Environment Map Materialien
const convertSingleMaterial = (originalMaterial: Material): Material => {
  // Wenn es bereits ein MeshStandardMaterial ist, Environment Map Parameter optimieren
  if (originalMaterial instanceof MeshStandardMaterial) {
    const modernMaterial = originalMaterial.clone();
    modernMaterial.envMapIntensity = modernMaterial.envMapIntensity || 1.0;
    modernMaterial.metalness = modernMaterial.metalness || 0.9;
    modernMaterial.roughness = modernMaterial.roughness || 0.5;
    return modernMaterial;
  }

  // Für andere Materialtypen: in MeshStandardMaterial konvertieren
  const modernMaterial = new MeshStandardMaterial();

  // Basis-Eigenschaften übernehmen mit Type Guards
  if (hasProperty(originalMaterial, "color") && originalMaterial.color) {
    modernMaterial.color.copy(originalMaterial.color);
  }
  if (hasProperty(originalMaterial, "map") && originalMaterial.map) {
    modernMaterial.map = originalMaterial.map;
  }
  if (
    hasProperty(originalMaterial, "normalMap") &&
    originalMaterial.normalMap
  ) {
    modernMaterial.normalMap = originalMaterial.normalMap;
  }
  if (
    hasProperty(originalMaterial, "transparent") &&
    originalMaterial.transparent !== undefined
  ) {
    modernMaterial.transparent = originalMaterial.transparent;
  }
  if (
    hasProperty(originalMaterial, "opacity") &&
    originalMaterial.opacity !== undefined
  ) {
    modernMaterial.opacity = originalMaterial.opacity;
  }
  if (
    hasProperty(originalMaterial, "side") &&
    originalMaterial.side !== undefined
  ) {
    modernMaterial.side = originalMaterial.side;
  }

  // Moderne PBR-Eigenschaften setzen
  modernMaterial.metalness = 0.9;
  modernMaterial.roughness = 0.5;
  modernMaterial.envMapIntensity = 1.0;

  return modernMaterial;
};

/** Converts legacy materials to modern PBR; exported for PianoHingeRenderer. */
export const convertToModernMaterial = (
  originalMaterial: Material | Material[],
): Material | Material[] => {
  if (Array.isArray(originalMaterial)) {
    return originalMaterial.map((mat) => convertSingleMaterial(mat));
  }
  return convertSingleMaterial(originalMaterial);
};

/**
 * Renders both sides of each face with the same maps (open shells / single-sided UVs).
 */
export const setMaterialsDoubleSided = (
  material: Material | Material[] | null | undefined,
): void => {
  if (material == null) return;
  const list = Array.isArray(material) ? material : [material];
  for (const m of list) {
    m.side = DoubleSide;
    m.needsUpdate = true;
  }
};

interface SupplyPartBoundsInfo {
  width: number;
  height: number;
  depth: number;
  offset: [number, number, number];
}

/**
 * Disposes the GPU resources we *own* on a subtree before dropping it.
 *
 * Materials here are always per-load instances (`material.clone()` /
 * `convertToModernMaterial()` create fresh ones for every child), so disposing
 * them is safe and necessary — otherwise each rebuild orphans them and the GPU
 * accumulates programs/uniform buffers, making drags progressively laggier.
 *
 * Geometry is deliberately NOT disposed: `ThreeToolsInstance.loadMesh()` hands
 * back `mesh.clone()`, and `Object3D.clone()` keeps the *shared* geometry from
 * the mesh cache. Disposing it would corrupt every future load of that model.
 */
const disposeOwnedMaterials = (object: Object3D) => {
  object.traverse((child) => {
    const mesh = child as Mesh;
    if (!mesh.isMesh) return;
    const { material } = mesh;
    if (Array.isArray(material)) {
      for (const m of material) m?.dispose?.();
    } else {
      material?.dispose?.();
    }
  });
};

const clearGroupChildren = (group: Group) => {
  while (group.children.length > 0) {
    const child = group.children[0];
    if (child) {
      group.remove(child);
      disposeOwnedMaterials(child);
    }
  }
};

const parseFiniteNumber = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return null;
};

const extractSupplyPartBounds = (
  bounds: unknown,
): SupplyPartBoundsInfo | null => {
  if (!bounds || typeof bounds !== "object") {
    return null;
  }

  const boundsRecord = bounds as Record<string, unknown>;
  const width = parseFiniteNumber(boundsRecord.width);
  const height = parseFiniteNumber(boundsRecord.height);
  const depth = parseFiniteNumber(boundsRecord.depth);

  if (
    width == null ||
    height == null ||
    depth == null ||
    width <= 0 ||
    height <= 0 ||
    depth <= 0
  ) {
    return null;
  }

  const offsetX = parseFiniteNumber(boundsRecord.offsetX) ?? 0;
  const offsetY = parseFiniteNumber(boundsRecord.offsetY) ?? 0;
  const offsetZ = parseFiniteNumber(boundsRecord.offsetZ) ?? 0;

  return {
    width,
    height,
    depth,
    offset: [offsetX, offsetY, offsetZ],
  };
};

const extractSupplyPartBoundsFromPartSelection = (
  partSelection: IListUserOption | undefined,
): SupplyPartBoundsInfo | null => {
  if (!partSelection) return null;
  const optionRecord = partSelection as Record<string, unknown>;
  return extractSupplyPartBounds(optionRecord.bounds);
};

/** Applied to loaded mesh before adding to scene; exported for PianoHingeRenderer. */
export const applyMeshLocalTransform = (
  object: Object3D,
  meshInfo: IMeshInfo & { id: string },
): Object3D => {
  if (meshInfo.transform?.scale != null) {
    object.scale.set(
      meshInfo.transform.scale,
      meshInfo.transform.scale,
      meshInfo.transform.scale,
    );
  }

  const sunlabsNormalize = new Object3D();
  const offset = meshInfo.transform?.position || [0, 0, 0];
  sunlabsNormalize.position.set(-offset[0], -offset[2], offset[1]);

  if (meshInfo.transform?.rotation != null) {
    sunlabsNormalize.rotation.set(
      ThreeToolsInstance.DEG2RAD(meshInfo.transform.rotation[0]),
      ThreeToolsInstance.DEG2RAD(meshInfo.transform.rotation[1]),
      ThreeToolsInstance.DEG2RAD(meshInfo.transform.rotation[2]),
    );
  }

  sunlabsNormalize.add(object);

  const localTransformation = new Object3D();
  localTransformation.add(sunlabsNormalize);
  localTransformation.rotation.set(ThreeToolsInstance.DEG2RAD(180), 0, 0);

  return localTransformation;
};

const createSupplyPartLoadingPlaceholder = (
  meshInfo: IMeshInfo & { id: string },
  loadingBounds: SupplyPartBoundsInfo | null,
): { object: Object3D; dispose: () => void } => {
  if (loadingBounds) {
    const placeholderGeometry = new BoxGeometry(
      loadingBounds.width,
      loadingBounds.height,
      loadingBounds.depth,
    );
    const placeholderMaterial = new MeshBasicMaterial({
      transparent: true,
      opacity: 0,
      colorWrite: false,
      depthWrite: false,
      depthTest: false,
    });
    const placeholderMesh = new Mesh(placeholderGeometry, placeholderMaterial);
    placeholderMesh.position.set(...loadingBounds.offset);
    placeholderMesh.name = PRODUCT_SCENE_LOADING_BOUNDS_NAME;

    return {
      object: applyMeshLocalTransform(placeholderMesh, meshInfo),
      dispose: () => {
        placeholderGeometry.dispose();
        placeholderMaterial.dispose();
      },
    };
  }

  const marker = new Object3D();
  marker.name = PRODUCT_SCENE_LOADING_BOUNDS_NAME;

  return {
    object: applyMeshLocalTransform(marker, meshInfo),
    dispose: () => {},
  };
};

interface SupplyPartRendererProps {
  id: string;
  /**
   * Forces a material from `manufacturer.materials` (e.g.
   * {@link MESH_INFO_MATERIAL_SHEETMETAL_KEY}) regardless of the mesh override.
   * When omitted the renderer keeps the original GLTF / override material.
   */
  forcedMaterialKey?: string;
}

// Interne Implementierung ohne Memo
const SupplyPartRendererImpl = ({
  id,
  forcedMaterialKey,
}: SupplyPartRendererProps) => {
  // Alle Hooks IMMER in derselben Reihenfolge aufrufen
  const useProductStore = useProductStoreContext();
  const manufacturer = useContext(ManufacturerContext);
  const groupRef = useRef<Group>(null);

  // Use useShallow to select part definition directly - ensures re-render when part changes
  const partDef = useProductStore(
    useShallow((state) => {
      if (!state.product?.partDefinitions || !id) return null;
      const definition = state.product.partDefinitions.get(id);
      return definition as SupplyPartDefinition<string, IListUserOption> | null;
    }),
  );

  // `AbstractProduct`'s incremental build hands us a *new* `partDef` object on
  // every drag-frame rebuild (parts with interactions are `cloneDeep`d), even
  // when nothing about the mesh changed. Deriving meshInfo/loadingBounds
  // straight off `partDef` would then produce a new object identity every
  // frame, re-running the mesh-loading effect below — which re-clones the GLTF
  // tree and allocates fresh materials each time. That is the drag memory leak:
  // the GPU keeps accumulating orphaned materials, so dragging gets steadily
  // laggier until a reload. Key the derivations off the *values* that actually
  // affect loading so their identity stays stable across no-op rebuilds.
  const meshInfoKey = useMemo(() => {
    const mesh = partDef?.partSelection?.meshes?.default;
    if (!mesh?.url) return null;
    return JSON.stringify({
      url: mesh.url,
      name: mesh.name,
      transform: mesh.transform ?? null,
      bounds: mesh.bounds ?? null,
      materialOverride: mesh.materialOverride ?? null,
    });
  }, [partDef]);

  const partSelectionBoundsKey = useMemo(() => {
    const selection = partDef?.partSelection as
      | Record<string, unknown>
      | undefined;
    const bounds = selection?.bounds;
    return bounds ? JSON.stringify(bounds) : null;
  }, [partDef]);

  // Extract mesh info directly from inline option data
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-derive only when the mesh *value* (meshInfoKey) changes, not on every cloneDeep'd partDef identity.
  const meshInfo = useMemo((): (IMeshInfo & { id: string }) | null => {
    const mesh = partDef?.partSelection?.meshes?.default;
    if (!meshInfoKey || !mesh?.url) return null;
    return {
      ...mesh,
      id: mesh.name,
    };
  }, [meshInfoKey]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed by stable value keys (meshInfo + partSelectionBoundsKey) to avoid per-frame churn.
  const loadingBounds = useMemo(() => {
    const meshBounds = extractSupplyPartBounds(meshInfo?.bounds);
    if (meshBounds) {
      return meshBounds;
    }
    // Fallback for legacy data written on option level
    return extractSupplyPartBoundsFromPartSelection(partDef?.partSelection);
  }, [meshInfo, partSelectionBoundsKey]);

  // Material-Berechnung mit stabiler Referenz
  const material = useMemo(() => {
    if (!meshInfo || !manufacturer?.materials) return undefined;

    let mat: Material | undefined;
    const overrideKey = meshInfo.materialOverride;
    if (overrideKey && !isMeshInfoOriginalMaterial(overrideKey)) {
      mat = manufacturer.materials[overrideKey]?.material;
    }

    if (forcedMaterialKey) {
      const forced = manufacturer.materials[forcedMaterialKey]?.material;
      if (forced) {
        mat = forced;
      }
    }
    return mat;
  }, [meshInfo, manufacturer?.materials, forcedMaterialKey]);

  // Effect für Mesh-Loading (Hook wird immer aufgerufen)
  useEffect(() => {
    if (!groupRef.current || !meshInfo?.url) return;

    const group = groupRef.current;
    let cancelled = false;
    let disposePlaceholder = () => {};
    let releaseLoad: (() => void) | null = null;

    clearGroupChildren(group);

    const placeholder = createSupplyPartLoadingPlaceholder(
      meshInfo,
      loadingBounds,
    );
    group.add(placeholder.object);
    disposePlaceholder = placeholder.dispose;

    const loadMesh = async () => {
      releaseLoad = trackProductSceneMeshLoad(group);
      let loaded = false;
      try {
        const mesh = await ThreeToolsInstance.loadMesh({
          model: meshInfo.url,
        });

        if (!mesh || !groupRef.current || cancelled) {
          return;
        }

        if (mesh instanceof Mesh && mesh.geometry) {
          mesh.geometry.computeBoundingSphere();
        }

        mesh.traverse((child: Mesh | Object3D) => {
          if (child instanceof Mesh) {
            // Priorisierung: 1. Explizites Material-Override, 2. Original Material modernisiert
            if (material) {
              child.material = material.clone();
            } else if (child.material) {
              // Original-Material in moderne Environment Map Variante konvertieren
              child.material = convertToModernMaterial(child.material);
            }
            setMaterialsDoubleSided(child.material);
          }
        });

        const localTransformation = applyMeshLocalTransform(mesh, meshInfo);

        clearGroupChildren(groupRef.current);
        groupRef.current.add(localTransformation);
        disposePlaceholder();
        disposePlaceholder = () => {};
        loaded = true;
      } catch (error) {
        console.error("Could not load mesh:", meshInfo.url, error);
      } finally {
        if (!loaded && !cancelled && groupRef.current) {
          clearGroupChildren(groupRef.current);
          disposePlaceholder();
          disposePlaceholder = () => {};
        }
        releaseLoad?.();
        releaseLoad = null;
      }
    };

    void loadMesh();

    return () => {
      cancelled = true;
      releaseLoad?.();
      releaseLoad = null;
      disposePlaceholder();
      disposePlaceholder = () => {};
      if (groupRef.current) {
        clearGroupChildren(groupRef.current);
      }
    };
    // Include meshInfo.url explicitly to ensure reload when mesh URL changes
  }, [loadingBounds, meshInfo, meshInfo?.url, material]);

  // Validierung nach allen Hook-Aufrufen - return leere group statt null
  if (!partDef?.partSelection?.meshes || !meshInfo || !manufacturer) {
    return <group ref={groupRef} />;
  }

  return (
    <group ref={groupRef}>
      <Suspense fallback={null} />
    </group>
  );
};

// Memorisierte Export-Komponente mit stabilen Props
export const SupplyPartRenderer = memo(
  SupplyPartRendererImpl,
  (prevProps, nextProps) => {
    // Nur re-rendern wenn sich die relevanten Props ändern
    return (
      prevProps.id === nextProps.id &&
      prevProps.forcedMaterialKey === nextProps.forcedMaterialKey
    );
  },
);
